import { Settings, Camera, Dna, Users, Target, Lightbulb, TrendingUp, Shield, Clock, DollarSign, BarChart3, Cpu, Database, AlertTriangle } from 'lucide-react';
import type { RawCaseStudy } from '../../types/case-study';

export const SOLUTIONS_DATA = [
  {
    icon: <Settings className="w-8 h-8" />,
    title: "Predictive Maintenance",
    description: "Prevent equipment failures before they happen with AI-powered predictive analytics and real-time monitoring systems.",
    features: [
      "Real-time equipment monitoring",
      "Failure prediction algorithms",
      "Maintenance scheduling optimization",
      "Cost reduction up to 30%"
    ],
    link: "/solutions/predictive-maintenance"
  },
  {
    icon: <Camera className="w-8 h-8" />,
    title: "Computer Vision",
    description: "Advanced image processing and analysis for quality control, automation, and intelligent visual inspection systems.",
    features: [
      "Defect detection & classification",
      "Automated quality control",
      "Real-time image processing",
      "Custom model training"
    ],
    link: "/solutions/computer-vision"
  },
  {
    icon: <Dna className="w-8 h-8" />,
    title: "Bioinformatics",
    description: "Accelerate research with AI-driven genomic analysis, protein modeling, and drug discovery platforms.",
    features: [
      "Genomic sequence analysis",
      "Protein structure prediction",
      "Drug discovery acceleration",
      "Clinical data integration"
    ],
    link: "/solutions/bioinformatics"
  }
];

export const COMPANY_VALUES = [
  {
    icon: <Lightbulb className="w-8 h-8" />,
    title: "Innovation",
    description: "We push the boundaries of what's possible with AI, constantly exploring new frontiers and breakthrough technologies."
  },
  {
    icon: <Target className="w-8 h-8" />,
    title: "Precision",
    description: "Every solution we deliver is meticulously crafted with attention to detail and rigorous testing standards."
  },
  {
    icon: <Users className="w-8 h-8" />,
    title: "Collaboration",
    description: "We believe the best solutions emerge from diverse teams working together towards common goals."
  }
];

export const PREDICTIVE_MAINTENANCE_FEATURES = [
  {
    icon: <TrendingUp className="w-6 h-6" />,
    title: "Failure Prediction",
    description: "Predict equipment failures 2-8 weeks in advance with 95%+ accuracy"
  },
  {
    icon: <Clock className="w-6 h-6" />,
    title: "Real-time Monitoring",
    description: "24/7 monitoring of critical equipment parameters and performance metrics"
  },
  {
    icon: <DollarSign className="w-6 h-6" />,
    title: "Cost Optimization",
    description: "Reduce maintenance costs by up to 30% through optimized scheduling"
  },
  {
    icon: <Shield className="w-6 h-6" />,
    title: "Risk Mitigation",
    description: "Minimize unplanned downtime and prevent catastrophic equipment failures"
  }
];

export const PROCESS_STEPS = [
  {
    step: 1,
    title: "Data Collection",
    description: "Sensors capture vibration, temperature, pressure, and operational data",
    icon: <Database className="w-8 h-8" />
  },
  {
    step: 2,
    title: "AI Analysis",
    description: "Machine learning models analyze patterns and detect anomalies",
    icon: <Cpu className="w-8 h-8" />
  },
  {
    step: 3,
    title: "Prediction & Alerts",
    description: "System generates predictions and sends actionable alerts",
    icon: <AlertTriangle className="w-8 h-8" />
  },
  {
    step: 4,
    title: "Maintenance Planning",
    description: "Optimize maintenance schedules based on predicted failures",
    icon: <BarChart3 className="w-8 h-8" />
  }
];

export const CASE_STUDIES: RawCaseStudy[] = [
  {
    id: "evolution-of-ai",
    title: "The Evolution of AI: From Data to Super Intelligence",
    subtitle: "A comprehensive journey through the eight stages of artificial intelligence development",
    category: "AI Research",
    author: "amasQIS Research Team",
    publishDate: "January 15, 2025",
    readTime: "12 min read",
    tags: ["Artificial Intelligence", "Machine Learning", "AGI", "Super Intelligence", "Technology Evolution"],
    featuredImage: "https://images.unsplash.com/photo-1677442136019-21780ecad995?w=800&h=600&fit=crop",
    summary: "Artificial Intelligence (AI) is more than just a buzzword—it's a rapidly evolving force that's transforming how we live, work, and think. From the earliest use of statistics to the futuristic dream of superintelligent systems, AI's journey is both fascinating and vital to understand.",
    content: {
      sections: [
        {
          id: "introduction",
          title: "The Foundation of Intelligence",
          content: [
            "Artificial Intelligence (AI) is more than just a buzzword—it's a rapidly evolving force that's transforming how we live, work, and think. From the earliest use of statistics to the futuristic dream of superintelligent systems, AI's journey is both fascinating and vital to understand.",
            "Let's embark on this journey through the eight stages of AI, enriched with insights, imagination, and a vision of what lies ahead."
          ]
        },
        {
          id: "descriptive-inferential-statistics",
          title: "1. The Foundation: Descriptive & Inferential Statistics",
          content: "At the heart of AI lies the power of statistics. Descriptive statistics summarize historical data, distilling it into meaningful insights through metrics like averages or trends. Inferential statistics, on the other hand, enable predictions by drawing conclusions from samples. Together, they transform raw data into actionable knowledge, forming the foundation for all AI advancements."
        },
        {
          id: "intelligence",
          title: "2. Intelligence: The Spark of Cognition",
          content: "Intelligence is the ability to learn, reason, and adapt to new situations. In AI, this translates to systems that process information and make decisions. This foundational stage sets the stage for AI's evolution, enabling machines to move beyond rote computation to purposeful action."
        },
        {
          id: "statistical-learning",
          title: "3. Statistical Learning: The Pattern-Seeking Engine",
          content: "Statistical learning is where AI begins to shine. This stage involves algorithms that detect patterns and trends in vast datasets, forming the backbone of machine learning. By improving through experience without explicit programming, these systems empower AI to tackle complex tasks, from image recognition to predictive analytics."
        },
        {
          id: "agent-systems",
          title: "4. Agent & Multi-Agent Systems: The Collaborative Ecosystem",
          content: "AI doesn't always work alone. Agent systems are individual AI entities that perceive and act within an environment. In multi-agent systems, these agents interact—cooperating or competing to achieve shared or conflicting goals. Think of a bustling city where each agent is a citizen, contributing to a larger, dynamic system."
        },
        {
          id: "narrow-intelligence",
          title: "5. Narrow Intelligence: The Master Specialist",
          content: "Narrow Intelligence represents AI's current pinnacle in specific domains. These systems excel at single tasks, like diagnosing diseases or mastering chess. While they outperform humans in their niche, their capabilities are tightly constrained, lacking the flexibility to handle unrelated challenges."
        },
        {
          id: "general-intelligence",
          title: "6. General Intelligence: The Versatile Thinker",
          content: "General Intelligence marks a monumental leap. This stage envisions AI with human-like adaptability, capable of learning any task and solving diverse problems across domains. It's the bridge between narrow expertise and universal competence, a milestone that remains theoretical but tantalizingly close."
        },
        {
          id: "agi",
          title: "7. AGI (Artificial General Intelligence): The Human-Like Mind",
          content: "Artificial General Intelligence (AGI) is the holy grail of AI research. AGI would match or surpass human cognitive flexibility, learning and applying knowledge across an unlimited range of tasks. From writing poetry to solving scientific mysteries, AGI would rival the resourcefulness of the human mind."
        },
        {
          id: "super-agi",
          title: "8. Super AGI: Beyond Human Limits",
          content: "Super AGI is the ultimate frontier. This stage imagines AI that surpasses human intelligence in every domain—creativity, problem-solving, and beyond. Super AGI could redefine reality, solving problems we can't yet comprehend. While speculative, it raises profound questions about ethics, control, and humanity's role in an AI-driven future."
        },
        {
          id: "bonus-concepts",
          title: "Bonus Concepts: Universal Intelligence and Cyborgs",
          type: "list",
          content: [
            "Universal Intelligence: A theoretical pinnacle, this concept describes AI with boundless adaptability. Given infinite resources, it could master any environment or challenge, transcending all limitations.",
            "Cyborgs: The fusion of human and machine, cyborgs blend AI's computational power with human intuition. This hybrid approach could redefine what it means to be human, amplifying our capabilities through technology."
          ]
        },
        {
          id: "importance",
          title: "Why These Stages Matter",
          content: [
            "The eight stages of AI provide a roadmap for understanding its evolution. From the statistical roots that power today's algorithms to the speculative heights of Super AGI, each stage builds on the last, pushing the boundaries of what's possible.",
            "As we advance, we must balance innovation with responsibility, ensuring AI serves humanity's best interests. The journey from data to superintelligence is not just about technological progress—it's about shaping the future of human civilization itself."
          ]
        }
      ]
    },
    // Legacy fields for backward compatibility with existing CaseStudyCard component
    client: "amasQIS Research",
    industry: "AI Research",
    duration: "3 months",
    teamSize: "5 researchers",
    description: "Comprehensive analysis and roadmap of AI development stages with practical insights",
    challenge: "Understanding the complex evolution of artificial intelligence from basic statistics to superintelligence",
    solution: "Comprehensive analysis and roadmap of AI development stages with practical insights",
    results: ["8 distinct AI evolution stages identified", "Clear progression pathway established", "Future implications outlined"],
    technologies: ["Machine Learning", "Deep Learning", "Natural Language Processing", "Computer Vision"],
    image: "https://images.unsplash.com/photo-1677442136019-21780ecad995?w=600&h=400&fit=crop",
    metrics: [
      { label: "AI Stages", value: "8", description: "Distinct AI evolution stages identified" },
      { label: "Research Duration", value: "12+", description: "Months of comprehensive analysis" }
    ]
  }
];
